/**
 * WorkRateCalcEditor.tsx
 *
 * 土地計算書・建物計算書の構造化入力エディタ（fee-schedules 設計書 §1）。
 * 料金表（WorkRateSchedule）から作業種別を選び、数量・面積・加減率を入力すると
 * computeWorkRate() でライブ計算する。計算式・単価はすべて料金表側にあり、
 * この画面では金額を直接入力しない（現行Excelの「1部入力すると自動計算」の移植）。
 *
 * - editable=true: schedule 必須。行の追加/削除と下部集計フィールドを編集できる
 * - editable=false: result（発行済みは calc_result_json スナップショット）のみから
 *   読み取り専用で描画する。発行後に料金表が変わっても表示金額は変わらない
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  computeWorkRate,
  formatJPY,
  type WorkRateInput,
  type WorkRateInputRow,
  type WorkRateItem,
  type WorkRateResult,
  type WorkRateSchedule,
} from "@juro/shared";
import { emptyWorkRateInput } from "../../lib/feeCalcDefaults.js";
import {
  WorkItemCombobox,
  type ComboboxOption,
  type WorkItemComboboxHandle,
} from "../ui/WorkItemCombobox.js";

const thStyle: React.CSSProperties = { padding: "7px 8px", textAlign: "left", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "5px 8px" };
const inputStyle: React.CSSProperties = { padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 5, fontSize: 13 };
const numInputStyle: React.CSSProperties = { ...inputStyle, width: 90, textAlign: "right" };

function fmtQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/** 1行だけの入力で計算し、その行の基準額・金額を得る（0数量などで行が落ちたら null） */
function computeRow(schedule: WorkRateSchedule, row: WorkRateInputRow) {
  const res = computeWorkRate(schedule, { ...emptyWorkRateInput(), rows: [row] });
  return res.rows[0] ?? null;
}

function num(v: string): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * 小数入力欄（面積・加減率・数量）。
 * `<input type="number">` は「150.」のような小数点直後の中間状態で value が空文字になり、
 * そのままパースすると入力途中の値が 0 に潰れる。編集中はローカルの生文字列を保持し、
 * 有効な数値になったときだけ親へ反映する（フォーカス外で外部値と同期）。
 */
function DecimalInput({
  value, onValue, style, placeholder,
}: {
  /** null = 未入力 */
  value: number | null;
  onValue: (v: number | null) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}) {
  const [text, setText] = useState(value === null ? "" : String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value === null ? "" : String(value));
  }, [value, focused]);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const t = e.target.value;
        if (!/^\d*\.?\d*$/.test(t)) return;
        setText(t);
        if (t === "" || t === ".") { onValue(null); return; }
        const n = Number(t);
        if (Number.isFinite(n)) onValue(n);
      }}
      style={style}
    />
  );
}

// ── 下部集計テーブル（編集・読み取り共通） ──────────────────────────────────
function TotalsTable({ result }: { result: WorkRateResult }) {
  const rows: { label: string; value: number; emphasis?: boolean }[] = [
    { label: "上記の合計（作業＋旅費）", value: result.gross_total },
    { label: "① 合計報酬額（調整後）", value: result.fee_total },
    { label: "② 消費税（10%）", value: result.consumption_tax },
    { label: "③ 小計（①＋②）", value: result.subtotal_with_tax },
    { label: "④ 源泉所得税", value: result.withholding_tax },
    { label: "⑤ 差引金額（③−④)", value: result.net_after_withholding },
    { label: "⑥ 登録免許税・印紙税＋立替金", value: result.expense_subtotal },
    { label: "⑦ 合計金額（⑤＋⑥）", value: result.grand_total },
    { label: "⑧ 前受金", value: result.prepaid_amount },
    { label: "⑨ 請求金額（⑦−⑧）", value: result.billed_total, emphasis: true },
  ];
  return (
    <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
      <table style={{ minWidth: 320, borderCollapse: "collapse", fontSize: 13 }}>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={{ padding: "6px 10px", borderBottom: "1px solid #e2e8f0", color: "#475569", fontWeight: r.emphasis ? 800 : 600 }}>
                {r.label}
              </td>
              <td style={{ padding: "6px 10px", borderBottom: "1px solid #e2e8f0", textAlign: "right", color: r.emphasis ? "#0f766e" : "#0f172a", fontWeight: r.emphasis ? 900 : 700 }}>
                {formatJPY(r.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── 読み取り専用表示（発行済み: calc_result スナップショットのみから描画） ──
function ReadOnlyView({ input, result }: { input: WorkRateInput; result: WorkRateResult | null }) {
  if (!result) {
    return <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>計算結果がありません</p>;
  }
  return (
    <div>
      {(input.location || input.area_note) && (
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 10 }}>
          {input.location && <span style={{ marginRight: 16 }}>所在: {input.location}</span>}
          {input.area_note && <span>{input.area_note}</span>}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 520 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              <th style={thStyle}>作業種別</th>
              <th style={{ ...thStyle, textAlign: "right" }}>数量・面積</th>
              <th style={thStyle}>単位</th>
              <th style={{ ...thStyle, textAlign: "right" }}>単価・基準額</th>
              <th style={{ ...thStyle, textAlign: "right" }}>加減率</th>
              <th style={{ ...thStyle, textAlign: "right" }}>金額</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((r, i) => (
              <tr key={`${r.key}_${i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={tdStyle}>{r.label}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{fmtQty(r.quantity)}</td>
                <td style={tdStyle}>{r.unit}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatJPY(r.base_amount)}</td>
                <td style={{ ...tdStyle, textAlign: "right", color: "#64748b" }}>{r.factor ?? "—"}</td>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{formatJPY(r.amount)}</td>
              </tr>
            ))}
            {result.travel_amount > 0 && (
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={tdStyle}>旅費</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{fmtQty(result.travel_km)}</td>
                <td style={tdStyle}>km</td>
                <td style={{ ...tdStyle, textAlign: "right" }} />
                <td style={{ ...tdStyle, textAlign: "right", color: "#64748b" }}>—</td>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{formatJPY(result.travel_amount)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TotalsTable result={result} />
    </div>
  );
}

// ── 本体 ────────────────────────────────────────────────────────────────────
export function WorkRateCalcEditor({
  schedule, input, result, onChange, editable,
}: {
  /** 現在の料金表（編集時は必須。読み取り専用表示では未使用） */
  schedule: WorkRateSchedule | null;
  input:    WorkRateInput;
  /** draft はライブ計算値、issued は calc_result スナップショット */
  result:   WorkRateResult | null;
  onChange: (input: WorkRateInput) => void;
  editable: boolean;
}) {
  /** 直前に選んだセクション。同一セクションを連続で足すのが土地計算書の実態（設計書 §6.5） */
  const [lastSection, setLastSection] = useState<string | null>(null);
  /** 面積段階行を足した直後に「1階」等の枝番へフォーカスを送るための予約 */
  const [focusSuffixIndex, setFocusSuffixIndex] = useState<number | null>(null);
  const suffixRefs = useRef(new Map<number, HTMLInputElement>());
  const comboRef = useRef<WorkItemComboboxHandle>(null);

  useEffect(() => {
    if (focusSuffixIndex === null) return;
    const el = suffixRefs.current.get(focusSuffixIndex);
    el?.focus();
    setFocusSuffixIndex(null);
  }, [focusSuffixIndex]);

  /**
   * 料金表の全作業をフラットな候補にする。
   * `optgroup` の階層を平坦化してインクリメンタル検索を効かせ、セクションはバッジで示す。
   * 単価は候補行に必ず出す（料金表側は元から出せていたので劣化させない）。
   */
  const workOptions = useMemo<ComboboxOption[]>(() => {
    if (!schedule) return [];
    const sectionLabel = new Map(schedule.sections.map((s) => [s.key, s.label]));
    const sectionOrder = new Map(schedule.sections.map((s, i) => [s.key, i]));
    const entries = schedule.items.map((item) => {
      const label = sectionLabel.get(item.section) ?? item.section;
      const recent = item.section === lastSection;
      const opt: ComboboxOption = {
        id: item.key,
        label: item.label,
        price: item.pricing.type === "fixed" ? item.pricing.unit_price : undefined,
        priceSuffix: item.pricing.type === "fixed" ? `/${item.unit}` : undefined,
        badge: label,
        badgeColor: recent ? "#0f766e" : "#475569",
        meta: [
          item.pricing.type === "area_tiered" ? `面積段階表（${item.unit}）` : null,
          item.note,
        ].filter(Boolean).join(" ・ ") || undefined,
        keywords: `${item.note ?? ""} ${label} ${item.key}`,
      };
      return { opt, section: item.section };
    });
    // 直前に使ったセクション → 料金表のセクション順 の優先度で並べる（sort は安定）
    return entries
      .sort((a, b) => {
        const recent = (s: string) => (s === lastSection ? 0 : 1);
        return (
          recent(a.section) - recent(b.section) ||
          (sectionOrder.get(a.section) ?? 99) - (sectionOrder.get(b.section) ?? 99)
        );
      })
      .map((e) => e.opt);
  }, [schedule, lastSection]);

  if (!editable) return <ReadOnlyView input={input} result={result} />;
  if (!schedule) {
    return <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>料金表を読み込み中…</p>;
  }

  const itemMap = new Map<string, WorkRateItem>(schedule.items.map((i) => [i.key, i]));

  function updateRow(index: number, patch: Partial<WorkRateInputRow>) {
    onChange({ ...input, rows: input.rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  }

  function removeRow(index: number) {
    onChange({ ...input, rows: input.rows.filter((_, i) => i !== index) });
  }

  function addRow(key: string) {
    const item = itemMap.get(key);
    if (!item) return;
    // area は未入力（空欄）から始める（DecimalInput が null=未入力を表示する）
    const row: WorkRateInputRow = { key, quantity: 1 };
    const newIndex = input.rows.length;
    onChange({ ...input, rows: [...input.rows, row] });
    // 選択はリセットせず、次の候補は同じセクションを先頭に寄せる
    setLastSection(item.section);
    // 面積段階行は「1階」等の枝番を続けて打つのが実態なのでそこへ送る。
    // それ以外は入力欄に留まり、連続で作業を足せる
    if (item.pricing.type === "area_tiered") setFocusSuffixIndex(newIndex);
  }

  // 表示はセクション順にグループ化する（input.rows の並びは保存順のまま）
  const grouped = schedule.sections
    .map((section) => ({
      section,
      rows: input.rows
        .map((row, index) => ({ row, index, item: itemMap.get(row.key) }))
        .filter((e): e is { row: WorkRateInputRow; index: number; item: WorkRateItem } =>
          !!e.item && e.item.section === section.key),
    }))
    .filter((g) => g.rows.length > 0);
  const orphanRows = input.rows
    .map((row, index) => ({ row, index }))
    .filter((e) => !itemMap.has(e.row.key));

  return (
    <div>
      {/* 所在・補足 */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>所在</span>
          <input type="text" value={input.location ?? ""} placeholder="例：岡崎市三崎町"
            onChange={(e) => onChange({ ...input, location: e.target.value || undefined })}
            style={{ ...inputStyle, width: 260 }} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>補足（地域区分・床面積表示など）</span>
          <input type="text" value={input.area_note ?? ""}
            onChange={(e) => onChange({ ...input, area_note: e.target.value || undefined })}
            style={{ ...inputStyle, width: 260 }} />
        </label>
      </div>

      {/* 作業行 */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              <th style={thStyle}>作業種別</th>
              <th style={{ ...thStyle, textAlign: "right" }}>数量・面積</th>
              <th style={thStyle}>単位</th>
              <th style={{ ...thStyle, textAlign: "right" }}>単価・基準額</th>
              <th style={{ ...thStyle, textAlign: "right" }}>加減率</th>
              <th style={{ ...thStyle, textAlign: "right" }}>金額</th>
              <th style={{ ...thStyle, width: 28 }} />
            </tr>
          </thead>
          <tbody>
            {grouped.map(({ section, rows }) => (
              [
                <tr key={`sec_${section.key}`} style={{ background: "#f8fafc" }}>
                  <td colSpan={7} style={{ ...tdStyle, fontWeight: 700, color: "#334155", fontSize: 12 }}>
                    {section.label}
                  </td>
                </tr>,
                ...rows.map(({ row, index, item }) => {
                  const isArea = item.pricing.type === "area_tiered";
                  const computed = computeRow(schedule, row);
                  return (
                    <tr key={`row_${index}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={tdStyle}>
                        <span>{item.label}</span>
                        {isArea && (
                          <input type="text" value={row.label_suffix ?? ""} placeholder="1階 等"
                            ref={(el) => {
                              if (el) suffixRefs.current.set(index, el);
                              else suffixRefs.current.delete(index);
                            }}
                            onChange={(e) => updateRow(index, { label_suffix: e.target.value || undefined })}
                            style={{ ...inputStyle, width: 70, marginLeft: 6, fontSize: 12, padding: "3px 6px" }} />
                        )}
                        {item.note && <div style={{ fontSize: 10, color: "#94a3b8" }}>{item.note}</div>}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {isArea ? (
                          <DecimalInput value={row.area ?? null}
                            onValue={(v) => updateRow(index, { area: v ?? undefined })}
                            style={numInputStyle} />
                        ) : (
                          <DecimalInput value={row.quantity === 0 ? null : row.quantity}
                            onValue={(v) => updateRow(index, { quantity: v ?? 0 })}
                            style={numInputStyle} />
                        )}
                      </td>
                      <td style={tdStyle}>{item.unit}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: "#475569" }}>
                        {item.pricing.type === "area_tiered"
                          ? (computed ? formatJPY(computed.base_amount) : "—")
                          : formatJPY(item.pricing.unit_price)}
                        {item.factor_mode === "surcharge" && item.surcharge_unit_price != null && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>加算 {formatJPY(item.surcharge_unit_price)}×率</div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <DecimalInput value={row.factor ?? null} placeholder="—"
                          onValue={(v) => updateRow(index, { factor: v ?? undefined })}
                          style={{ ...numInputStyle, width: 70 }} />
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                        {computed ? formatJPY(computed.amount) : "—"}
                      </td>
                      <td style={{ ...tdStyle, width: 28 }}>
                        <button onClick={() => removeRow(index)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16, padding: "0 4px" }}>×</button>
                      </td>
                    </tr>
                  );
                }),
              ]
            ))}
            {orphanRows.map(({ row, index }) => (
              // 料金表から削除された作業種別の行（保存済みデータの表示・削除のみ可能）
              <tr key={`orphan_${index}`} style={{ borderBottom: "1px solid #f1f5f9", background: "#fffbeb" }}>
                <td style={tdStyle} colSpan={5}>
                  <span style={{ color: "#92400e", fontSize: 12 }}>
                    ⚠ 現在の料金表にない作業種別です（{row.key}）。削除して選び直してください
                  </span>
                </td>
                <td style={{ ...tdStyle, textAlign: "right", color: "#94a3b8" }}>—</td>
                <td style={{ ...tdStyle, width: 28 }}>
                  <button onClick={() => removeRow(index)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16, padding: "0 4px" }}>×</button>
                </td>
              </tr>
            ))}
            {input.rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...tdStyle, color: "#94a3b8", fontSize: 12, padding: "12px 8px" }}>
                  下の入力欄に作業名を打つと候補が出ます（例:「境界」）。数量・面積を入力すると金額が自動計算されます。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 作業追加（打って探して Enter。料金表に無い作業は確定できない = 単価の唯一の源を守る） */}
      <div style={{ marginTop: 8, maxWidth: 420 }}>
        <WorkItemCombobox
          ref={comboRef}
          options={workOptions}
          onSelect={(opt) => addRow(opt.id)}
          allowFreeText={false}
          clearOnSelect
          placeholder="＋ 作業を追加（例: 境界）"
          emptyMessage="該当する作業がありません。料金表に無い作業は追加できません"
          aria-label="作業種別を追加"
          footer={
            <Link to="/settings/fee-schedules" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "none" }}>
              料金表を管理（単価の編集）→
            </Link>
          }
        />
      </div>

      {/* 旅費・調整額・立替 等 */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 16, padding: "12px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
            旅費 km {schedule.travel_deduct_km > 0 && `（−${schedule.travel_deduct_km}km 控除）`}
          </span>
          <input type="number" min={0} value={input.travel_km}
            onChange={(e) => onChange({ ...input, travel_km: num(e.target.value) })}
            style={numInputStyle} />
        </label>
        <div style={{ fontSize: 12, color: "#475569", paddingBottom: 6 }}>
          旅費額: <b>{formatJPY(result?.travel_amount ?? 0)}</b>
          <span style={{ color: "#94a3b8", marginLeft: 6 }}>（{formatJPY(schedule.travel_unit_price)}/km）</span>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>調整額（減算）</span>
          <input type="number" value={input.adjustment_amount}
            onChange={(e) => onChange({ ...input, adjustment_amount: Math.trunc(num(e.target.value)) })}
            style={numInputStyle} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>登録免許税・印紙税</span>
          <input type="number" min={0} value={input.registration_tax_amount}
            onChange={(e) => onChange({ ...input, registration_tax_amount: Math.max(0, Math.trunc(num(e.target.value))) })}
            style={numInputStyle} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>立替金</span>
          <input type="number" min={0} value={input.advance_expense_amount}
            onChange={(e) => onChange({ ...input, advance_expense_amount: Math.max(0, Math.trunc(num(e.target.value))) })}
            style={numInputStyle} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>前受金</span>
          <input type="number" min={0} value={input.prepaid_amount}
            onChange={(e) => onChange({ ...input, prepaid_amount: Math.max(0, Math.trunc(num(e.target.value))) })}
            style={numInputStyle} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={input.apply_withholding}
            onChange={(e) => onChange({ ...input, apply_withholding: e.target.checked })}
            style={{ cursor: "pointer" }} />
          <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>源泉徴収する（法人・給与支払者）</span>
        </label>
      </div>

      {result && <TotalsTable result={result} />}
    </div>
  );
}
